import { initialize } from 'launchdarkly-js-client-sdk';
import appConfig from 'appConfig';
import featureKeys from './featureKeys';

/**
 * Builds a service to access FeatureFlags
 */
export default (() => {
  let featureFlags = {};
  let _subscriptions = [];
  let ldclient = null;
  let isInitialized = false;
  let isInitializedPromiseResolve;

  let isInitializedPromise = new Promise((resolve) => {
    isInitializedPromiseResolve = resolve;
  });

  async function initializeFeatureFlags(user) {
    if (ldclient) {
      ldclient.close();
    }
    const ldOptions = {
      evaluationReasons: true,
      bootstrap: 'localstorage',
    };
    let ldUser = { anonymous: true };
    if (user) {
      const accountId = user.accountId || '';
      const firstName = user.givenName || '';
      const lastName = user.familyName || '';
      ldUser = { key: accountId, firstName, lastName };
    }

    ldclient = initialize(appConfig.getLaunchDarklyClientId(), ldUser, ldOptions);
    await ldclient.waitForInitialization();
    isInitializedPromiseResolve();
    featureFlags = ldclient.allFlags();
    isInitialized = true;

    //notify to all listeners who have already subscribed required flags
    _subscriptions.forEach((item) => {
      notifyListeners(item.flag);
    });

    /**
     * This Change event triggers when flag values are changed in LaunchDarkly.
     * updatedFlagDetails contains a map of updated feature flag keys and values. 
     * The map only contains the keys to flags that have changed.
     */
    ldclient.on('change', (updatedFlagDetails) => {
      Object.keys(updatedFlagDetails).forEach((key) => {
        featureFlags[key] = updatedFlagDetails[key].current;
        notifyListeners(key);
      });
    });
    return Object.freeze({...featureFlags});
  }

  function notifyListeners(featureFlagThatChanged) {
    _subscriptions.forEach((item) => {
      if (item.flag === featureFlagThatChanged) {
        item.callback(
          Object.freeze({
            flagName: featureFlagThatChanged,
            value: featureFlags[featureFlagThatChanged],
          }));
      }
    });
  }

  function unsubscribe(flag) {
    // This unsubscribe will unsubscribe everyone subscribed to this flag.
    _subscriptions = _subscriptions.filter((sub) => sub.flag !== flag);
  }

  function get() {
    if (!isInitialized) {
      throw new Error(
        "You tried to get the feature flags before the feature flag service got initialized"
      );
    }
    return Object.freeze({...featureFlags});
  }
  
  async function getWhenReady() {
    await isInitializedPromise;
    return Object.freeze({...featureFlags});
  }

  /**
   * @deprecated for React Components. 
   * Recommended only for services/hooks/util/reducers etc.
   * For React Components: 
   * Use getFeatureFlagValue method by consuming 'hocs/enhanceWithFeatureFlagSubscription'.
   * 
   * @param {String} featureName
   * @returns {Boolean} value of the feature from Launchdarkly
   */
  function is(featureName) {
    if (!isInitialized) {
      throw new Error(`You tried to check the status of ${featureName} before the service got initialized`);
    }
    if (!featureFlags.hasOwnProperty(featureName)) {
      throw new Error(`you tried to retrieve the status of an unknown feature flag ${featureName}`);
    }
    return featureFlags[featureName];
  }

  function set(data) {
    const { name, isSet } = data;
    featureFlags[name] = isSet;
    notifyListeners(name);
    return featureFlags[name];
  }

  function subscribe(featureFlag, callbackFn) {
    const newSubscription = {
      flag: featureFlag,
      callback: callbackFn,
    };
    _subscriptions.push(newSubscription);

    isInitializedPromise.then(() => {
      notifyListeners(featureFlag);
    });
    return ((flag) => unsubscribe(flag));
  }

  return {
    initializeFeatureFlags,
    subscribe,
    get,
    getWhenReady,
    is,
    set,
    unsubscribe,
  };
})();

export { featureKeys };
